/**
 * Admin list: official SpeedRun levels — rename, description, author, delete, open geometry editor.
 */
import { deleteAdminSpeedrunLevel, fetchSpeedrunLevels, updateAdminSpeedrunLevelMeta } from "../lib/cloud";
import { normalizeLevelData, type SpeedrunLevelDef } from "./speedrun-levels";

export type AdminSpeedrunLevelsUiOpts = {
    onLevelsChanged: () => void;
    onEditLayout: (levelIndex: number, def: SpeedrunLevelDef) => void;
};

export function mountAdminSpeedrunLevelsList(mount: HTMLElement, opts: AdminSpeedrunLevelsUiOpts) {
    const root = document.createElement("div");
    root.className = "adminSpeedrunLevelsList";

    async function refresh() {
        root.replaceChildren();
        const levels = await fetchSpeedrunLevels();
        if (!levels.length) {
            const p = document.createElement("p");
            p.className = "accessHint";
            p.textContent = "No official levels in the database yet.";
            root.appendChild(p);
            return;
        }
        for (const row of levels) {
            const rowEl = document.createElement("div");
            rowEl.className = "adminLevelRow modeBox";

            const title = document.createElement("div");
            title.className = "adminLevelRowTitle";
            title.textContent = `Level ${row.levelIndex}`;

            const nameLab = document.createElement("label");
            nameLab.className = "formLabel";
            nameLab.textContent = "Display name";

            const nameInp = document.createElement("input");
            nameInp.type = "text";
            nameInp.className = "formInput";
            nameInp.maxLength = 120;
            nameInp.placeholder = `Level ${row.levelIndex}`;
            nameInp.value = row.displayName ?? "";

            const descLab = document.createElement("label");
            descLab.className = "formLabel";
            descLab.textContent = "Description (shown on level card)";

            const descInp = document.createElement("textarea");
            descInp.className = "formInput adminLevelDescInput";
            descInp.maxLength = 500;
            descInp.rows = 3;
            descInp.placeholder = "Short description for the level picker card";
            descInp.value = row.description ?? "";

            const authorLab = document.createElement("label");
            authorLab.className = "formLabel";
            authorLab.textContent = "Author (Made by…)";

            const authorInp = document.createElement("input");
            authorInp.type = "text";
            authorInp.className = "formInput";
            authorInp.maxLength = 120;
            authorInp.placeholder = "Creator display name";
            authorInp.value = row.authorName ?? "";

            const btnRow = document.createElement("div");
            btnRow.className = "adminLevelRowBtns";

            const saveMeta = document.createElement("button");
            saveMeta.type = "button";
            saveMeta.className = "btn";
            saveMeta.textContent = "Save card info";

            const del = document.createElement("button");
            del.type = "button";
            del.className = "btn";
            del.textContent = "Delete level";

            const edit = document.createElement("button");
            edit.type = "button";
            edit.className = "btn green";
            edit.textContent = "Edit layout";

            saveMeta.onclick = () => {
                void (async () => {
                    try {
                        const v = nameInp.value.trim();
                        const d = descInp.value.trim();
                        const a = authorInp.value.trim();
                        await updateAdminSpeedrunLevelMeta(row.levelIndex, {
                            displayName: v.length ? v : null,
                            description: d.length ? d : null,
                            authorName: a.length ? a : null,
                        });
                        await refresh();
                        opts.onLevelsChanged();
                    } catch {
                        alert("Could not save card info (admin session / network).");
                    }
                })();
            };

            del.onclick = () => {
                if (!confirm(`Delete official level ${row.levelIndex} permanently?`)) return;
                void (async () => {
                    try {
                        await deleteAdminSpeedrunLevel(row.levelIndex);
                        await refresh();
                        opts.onLevelsChanged();
                    } catch {
                        alert("Could not delete (admin session / network).");
                    }
                })();
            };

            edit.onclick = () => {
                if (!row.data) {
                    alert("This level has no layout data.");
                    return;
                }
                opts.onEditLayout(row.levelIndex, normalizeLevelData(row.data));
            };

            btnRow.appendChild(saveMeta);
            btnRow.appendChild(del);
            btnRow.appendChild(edit);

            rowEl.appendChild(title);
            rowEl.appendChild(nameLab);
            rowEl.appendChild(nameInp);
            rowEl.appendChild(descLab);
            rowEl.appendChild(descInp);
            rowEl.appendChild(authorLab);
            rowEl.appendChild(authorInp);
            rowEl.appendChild(btnRow);
            root.appendChild(rowEl);
        }
    }

    mount.replaceChildren();
    mount.appendChild(root);
    void refresh();

    return {
        destroy() {
            mount.replaceChildren();
        },
        refresh: () => void refresh(),
    };
}
